import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import VehiculeForm from "./VehiculeForm";

export default function VehiculeList() {
  // États pour la gestion des données
  const [vehicules, setVehicules] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalVehicules, setTotalVehicules] = useState(0);

  // États pour la modale d'avis de recherche
  const [showModal, setShowModal] = useState(false);
  const [selectedVehicule, setSelectedVehicule] = useState(null);
  const [motif, setMotif] = useState("");

  // États pour la modale de modification
  const [showEditModal, setShowEditModal] = useState(false);
  const [editVehicule, setEditVehicule] = useState(null);

  // États pour les filtres de date
  const [dateFilter, setDateFilter] = useState("30days");
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [customDates, setCustomDates] = useState({
    start: "",
    end: ""
  });

  // État pour les toasts
  const [toast, setToast] = useState({ show: false, message: "", type: "info" });

  const navigate = useNavigate();
  const API_BASE = "https://srv968119.hstgr.cloud";
  const userStatu = localStorage.getItem("statu");

  // Fonction pour afficher un toast
  const showToast = (message, type = "info") => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: "", type: "info" });
    }, 4000);
  };

  // Options pour le nombre de lignes par page
  const limitOptions = [10, 50, 100, 500, 1000, 10000, 100000];

  // Récupère les véhicules avec les filtres actuels
  const fetchVehicules = async () => {
    try {
      let url = `${API_BASE}/vehicules?page=${page}&limit=${limit}&search=${encodeURIComponent(searchTerm.toUpperCase())}`;

      // Si aucun terme de recherche, appliquer les filtres de date
      if (!searchTerm.trim()) {
        const { startDate, endDate } = getDateRange();
        if (startDate) url += `&date_debut=${startDate.toISOString()}`;
        if (endDate) url += `&date_fin=${endDate.toISOString()}`;
      }

      const response = await fetch(url);
      const data = await response.json();
      setVehicules(data.vehicules);
      setTotalPages(data.totalPages);
      setTotalVehicules(data.totalVehicules);
    } catch (error) {
      console.error("Erreur lors de la récupération des véhicules :", error);
    }
  };

  // Calcule la plage de dates en fonction du filtre sélectionné
  const getDateRange = () => {
    const now = new Date();
    let startDate = null;
    let endDate = null;

    switch(dateFilter) {
      case "today":
        startDate = new Date(now.setHours(0, 0, 0, 0));
        endDate = new Date();
        break;
      case "yesterday":
        startDate = new Date(now);
        startDate.setDate(now.getDate() - 1);
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date(startDate);
        endDate.setHours(23, 59, 59, 999);
        break;
      case "7days":
        startDate = new Date(now);
        startDate.setDate(now.getDate() - 7);
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date();
        break;
      case "30days":
        startDate = new Date(now);
        startDate.setDate(now.getDate() - 30);
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date();
        break;
      case "thismonth":
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        endDate = new Date();
        break;
      case "lastmonth":
        startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        endDate = new Date(now.getFullYear(), now.getMonth(), 0);
        break;
      case "custom":
        startDate = customDates.start ? new Date(customDates.start) : null;
        endDate = customDates.end ? new Date(customDates.end) : null;
        break;
    }

    return { startDate, endDate };
  };

  // Gère la soumission des dates personnalisées
  const handleCustomDateSubmit = (e) => {
    e.preventDefault();
    setDateFilter("custom");
    setShowDatePicker(false);
    setPage(1); // Reset à la première page
  };

  // Exporte les données en CSV
  const exportToCSV = () => {
    const headers = ["ID", "Code Station", "Date Création", "TV12", "Franchise"];
    const rows = vehicules.map(v => [
      v.id_vehicule,
      v.code_station,
      new Date(v.date_creation).toLocaleDateString(),
      v.tv12 || "N/A",
      v.franchise
    ]);

    const csvContent = [headers.join(","), ...rows.map(row => row.join(","))].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `vehicules_${new Date().toISOString().slice(0,10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Gère l'envoi d'un avis de recherche
  const handleAvisDeRecherche = async () => {
    if (!selectedVehicule || !motif.trim()) {
      showToast("Veuillez renseigner un motif !", "error");
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/avis`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id_vehicule: selectedVehicule.id_vehicule,
          motif: motif.trim(),
          service_createur: localStorage.getItem("service"),
          date_creation: selectedVehicule.date_creation,
          code_station: selectedVehicule.code_station,
          tv12: selectedVehicule.tv12,
          franchise: selectedVehicule.franchise,
          couleur: selectedVehicule.couleur,
          chassis: selectedVehicule.chassis,
          nom: selectedVehicule.nom,
          prenom: selectedVehicule.prenom,
          numero_serie_plaque: selectedVehicule.numero_serie_plaque,
          telephone: selectedVehicule.telephone,
          usage: selectedVehicule.usage,
        }),
      });

      const data = await response.json();
      if (response.ok) {
        showToast("Avis de recherche envoyé avec succès !", "success");
        setShowModal(false);
        setMotif("");
      } else {
        showToast('Erreur: ' + data.message, "error");
      }
    } catch (error) {
      console.error("Erreur lors de l'envoi de l'avis de recherche :", error);
      showToast("Erreur serveur !", "error");
    }
  };

  // Gère la mise à jour d'un véhicule
  const handleUpdateVehicule = async () => {
    try {
      const response = await fetch(`${API_BASE}/vehicules/${editVehicule.id_vehicule}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(editVehicule),
      });

      if (response.ok) {
        fetchVehicules(); // Rafraîchir la liste
        setShowEditModal(false);
        showToast("Véhicule mis à jour avec succès !", "success");
      } else {
        const data = await response.json();
        showToast('Erreur: ' + data.message, "error");
      }
    } catch (error) {
      console.error("Erreur lors de la mise à jour du véhicule :", error);
      showToast("Erreur serveur !", "error");
    }
  };

  // Génère les numéros de pagination
  const getPaginationNumbers = () => {
    let start = Math.max(1, page - 2);
    let end = Math.min(totalPages, start + 4);

    if (totalPages > 5 && end === totalPages) {
      start = Math.max(1, end - 4);
    }

    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  // Effet pour recharger les données quand les filtres changent
  useEffect(() => {
    fetchVehicules();
  }, [searchTerm, page, limit, dateFilter, customDates]);

  return (
    <div className="px-3 sm:px-4 md:px-6 lg:px-8 py-4 bg-gray-100 dark:bg-gray-900 w-full flex flex-col rounded-xl">

      {/* Barre de filtres */}
      <div className="flex flex-wrap justify-between items-center mb-4 gap-2">
        <div className="flex flex-wrap gap-2">
          {/* Filtre par date */}
          <div className="dropdown z-40">
            <div tabIndex={0} role="button" className="btn btn-sm sm:btn-md">
              {{
                today: "Aujourd'hui",
                yesterday: "Hier",
                "7days": "7 derniers jours",
                "30days": "30 derniers jours",
                thismonth: "Ce mois-ci",
                lastmonth: "Mois précédent",
                custom: "Période perso"
              }[dateFilter] || "Filtrer par date"}
            </div>
            <ul tabIndex={0} className="dropdown-content menu shadow bg-base-100 rounded-box w-52">
              <li><button onClick={() => setDateFilter("today")}>Aujourd'hui</button></li>
              <li><button onClick={() => setDateFilter("yesterday")}>Hier</button></li>
              <li><button onClick={() => setDateFilter("7days")}>7 derniers jours</button></li>
              <li><button onClick={() => setDateFilter("30days")}>30 derniers jours</button></li>
              <li><button onClick={() => setDateFilter("thismonth")}>Ce mois-ci</button></li>
              <li><button onClick={() => setDateFilter("lastmonth")}>Mois précédent</button></li>
              <li><button onClick={() => setShowDatePicker(true)}>Période personnalisée</button></li>
            </ul>
          </div>

          {/* Sélecteur de nombre de lignes */}
          <div className="dropdown z-40">
            <div tabIndex={0} role="button" className="btn btn-sm sm:btn-md">
              {limit} lignes/page
            </div>
            <ul tabIndex={0} className="dropdown-content menu shadow bg-base-100 rounded-box w-40">
              {limitOptions.map(option => (
                <li key={option}>
                  <button onClick={() => {
                    setLimit(option);
                    setPage(1); // Reset à la première page
                  }}>
                    {option} lignes
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Date picker personnalisé */}
          {showDatePicker && (
            <div className="absolute z-50 mt-12 bg-white dark:bg-gray-700 p-4 rounded shadow-lg">
              <form onSubmit={handleCustomDateSubmit}>
                <div className="grid grid-cols-2 gap-2 mb-2">
                  <div>
                    <label className="block text-sm">Date de début</label>
                    <input
                      type="date"
                      className="input input-bordered w-full"
                      value={customDates.start}
                      onChange={(e) => setCustomDates({...customDates, start: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm">Date de fin</label>
                    <input
                      type="date"
                      className="input input-bordered w-full"
                      value={customDates.end}
                      onChange={(e) => setCustomDates({...customDates, end: e.target.value})}
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={() => setShowDatePicker(false)}
                  >
                    Annuler
                  </button>
                  <button type="submit" className="btn btn-primary">
                    Valider
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>

        {/* Bouton d'export */}
        <button onClick={exportToCSV} className="btn btn-primary btn-sm sm:btn-md">
          Exporter en CSV
        </button>
      </div>

      {/* Barre de recherche */}
      <div className="relative mb-4">
        <input
          type="text"
          placeholder="Rechercher un véhicule..."
          className="input input-bordered w-full input-sm sm:input-md"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setPage(1); // Reset à la première page
          }}
        />
        <div className="absolute right-2 sm:right-3 top-1.5 sm:top-3">
          <span className="badge badge-ghost">
            {totalVehicules} résultats
          </span>
        </div>
      </div>

      {/* Tableau des véhicules */}
      <div className="overflow-x-auto overflow-y-auto h-[28rem] bg-base-100 rounded-xl shadow border border-base-200">
        <table className="table table-zebra">
          <thead className="sticky top-0 z-10">
            <tr className="bg-base-200 text-xs sm:text-sm">
              <th className="whitespace-nowrap">ID</th>
              <th className="whitespace-nowrap">Code Station</th>
              <th className="hidden md:table-cell whitespace-nowrap">Franchise</th>
              <th className="hidden sm:table-cell whitespace-nowrap">Usage</th>
              <th className="whitespace-nowrap">Date Création</th>
              <th className="whitespace-nowrap">Actions</th>
            </tr>
          </thead>
          <tbody>
            {vehicules.map((vehicule) => (
              <tr key={vehicule.id_vehicule} className="hover text-xs sm:text-sm">
                <td className="font-medium">{vehicule.id_vehicule}</td>
                <td>{vehicule.code_station}</td>
                <td className="hidden md:table-cell">{vehicule.franchise}</td>
                <td className="hidden sm:table-cell">{vehicule.usage}</td>
                <td>{new Date(vehicule.date_creation).toLocaleDateString()}</td>
                <td>
                  <div className="dropdown dropdown-left">
                    <div tabIndex={0} role="button" className="btn btn-xs sm:btn-sm btn-ghost">
                      ⋮
                    </div>
                    <ul tabIndex={0} className="dropdown-content menu shadow bg-base-100 rounded-box w-48 sm:w-52">
                      <li>
                        <button onClick={() => navigate(`qrcd/${vehicule.id_vehicule}`)}>
                          Voir template
                        </button>
                      </li>
                      {(userStatu === 'controleur' || userStatu === 'admin') && (
                        <li>
                          <button onClick={() => {
                            setEditVehicule(vehicule);
                            setShowEditModal(true);
                          }}>
                            Modifier
                          </button>
                        </li>
                      )}
                      <li>
                        <button onClick={() => {
                          setSelectedVehicule(vehicule);
                          setShowModal(true);
                        }}>
                          Avis de recherche
                        </button>
                      </li>
                    </ul>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex justify-center mt-4">
        <div className="join">
          <button
            className="join-item btn btn-sm sm:btn-md"
            disabled={page === 1}
            onClick={() => setPage(p => p - 1)}
          >
            «
          </button>

          {getPaginationNumbers().map(num => (
            <button
              key={num}
              className={`join-item btn btn-sm sm:btn-md ${page === num ? 'btn-active' : ''}`}
              onClick={() => setPage(num)}
            >
              {num}
            </button>
          ))}

          <button
            className="join-item btn btn-sm sm:btn-md"
            disabled={page === totalPages}
            onClick={() => setPage(p => p + 1)}
          >
            »
          </button>
        </div>
      </div>

      {/* Modale Avis de recherche */}
      {showModal && (
        <div className="modal modal-open">
          <div className="modal-box max-w-lg">
            <h3 className="font-bold text-lg">Avis de recherche</h3>
            <p className="py-2">
              Motif pour le véhicule <span className="font-semibold">
                {selectedVehicule?.franchise} ({selectedVehicule?.code_station})
              </span>
            </p>
            <textarea
              className="textarea textarea-bordered w-full"
              placeholder="Décrivez le motif de recherche..."
              value={motif}
              onChange={(e) => setMotif(e.target.value)}
            />
            <div className="modal-action">
              <button className="btn" onClick={() => setShowModal(false)}>
                Annuler
              </button>
              <button className="btn btn-primary" onClick={handleAvisDeRecherche}>
                Confirmer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modale Modification de véhicule */}
      {showEditModal && (
        <div className="modal modal-open">
          <div className="modal-box max-w-2xl">
            <h3 className="font-bold text-lg">Modifier le véhicule</h3>
            <div className="py-4 flex flex-col gap-3">
              <label>Nom</label>
              <input
                type="text"
                className="input input-bordered w-full"
                value={editVehicule?.nom || ''}
                onChange={(e) => setEditVehicule({...editVehicule, nom: e.target.value})}
              />

              <label>Prénom</label>
              <input
                type="text"
                className="input input-bordered w-full"
                value={editVehicule?.prenom || ''}
                onChange={(e) => setEditVehicule({...editVehicule, prenom: e.target.value})}
              />

              <label>Téléphone</label>
              <input
                type="tel"
                className="input input-bordered w-full"
                value={editVehicule?.telephone || ''}
                onChange={(e) => setEditVehicule({...editVehicule, telephone: e.target.value})}
              />

              <label>Franchise</label>
              <input
                type="text"
                className="input input-bordered w-full"
                value={editVehicule?.franchise || ''}
                onChange={(e) => setEditVehicule({...editVehicule, franchise: e.target.value})}
              />

              <label>Couleur</label>
              <input
                type="text"
                className="input input-bordered w-full"
                value={editVehicule?.couleur || ''}
                onChange={(e) => setEditVehicule({...editVehicule, couleur: e.target.value})}
              />

              <label>Châssis</label>
              <input
                type="text"
                className="input input-bordered w-full"
                value={editVehicule?.chassis || ''}
                onChange={(e) => setEditVehicule({...editVehicule, chassis: e.target.value})}
              />

              <label>Numéro de Série Plaque</label>
              <input
                type="text"
                className="input input-bordered w-full"
                value={editVehicule?.numero_serie_plaque || ''}
                onChange={(e) => setEditVehicule({...editVehicule, numero_serie_plaque: e.target.value})}
              />

              <label>Usage</label>
              <input
                type="text"
                className="input input-bordered w-full"
                value={editVehicule?.usage || ''}
                onChange={(e) => setEditVehicule({...editVehicule, usage: e.target.value})}
              />

              <div className="modal-action flex justify-between">
                <button className="btn" onClick={() => setShowEditModal(false)}>
                  Annuler
                </button>
                <button className="btn btn-primary" onClick={handleUpdateVehicule}>
                  Enregistrer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toast.show && (
        <div className={`toast toast-top toast-end z-50`}>
          <div className={`alert ${
            toast.type === "success" ? "alert-success" : 
            toast.type === "error" ? "alert-error" : 
            "alert-info"
          }`}>
            <div>
              <span className="font-bold">
                {toast.type === "success" ? "✅" : 
                 toast.type === "error" ? "❌" : 
                 "ℹ️"}
              </span>
              <span>{toast.message}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
